// src/modules/app-views/crm/pages/Kanban/components/KanbanColumn.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef, useState } from 'react';
import { Badge, Input, Tooltip } from 'antd';
import { CloseOutlined, SearchOutlined, SettingOutlined } from '@ant-design/icons';
import { Droppable } from '@hello-pangea/dnd';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import KanbanCard from './KanbanCard';
import ColumnPropertiesModal from './modals/ColumnPropertiesModal';

// *******************************************************************************
// MODULE-LEVEL CACHE
// Persists search state across component unmount/remount (e.g. navigating to a
// card document and back). Lives as long as the browser tab is open.
// *******************************************************************************
const _columnSearchCache = new Map();

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const KanbanColumn = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    column,
    cards,
    boardUid,
    onDeleteColumn,
    onRenameColumn,
    onRefresh,
    autoRename,
    onAutoRenameDone,
    // Column DnD: true when the column is allowed to be dragged
    columnDraggable,
    // RBAC: admin/owner can always manage columns regardless of board permissions
    canManage,
    // Board permission (only enforced for guests): show/hide the column options menu
    allowColumnEdit,
    // True for index 0 (first column) — used to show the "+ New" button and protect from delete/drag
    isFirst,
    // Column DnD props from Draggable
    innerRef,
    draggableProps,
    dragHandleProps,
  } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const currentTheme = useZustand('theme', 'currentTheme');
  const colUid = column.uid || column._id;
  const colColor = column.color || '#8c8c8c';
  const isDark = currentTheme === 'dark';
  const colBg = hexToRgba(colColor, isDark ? 0.12 : 0.06);
  const colBgDragOver = hexToRgba(colColor, isDark ? 0.22 : 0.14);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [isRenaming, setIsRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(column.title);
  const [showProperties, setShowProperties] = useState(false);
  // Search state — initialized from cache so it survives navigation to a card and back
  const [isSearchOpen, setIsSearchOpen] = useState(
    () => _columnSearchCache.get(colUid)?.isOpen || false
  );
  const [searchQuery, setSearchQuery] = useState(
    () => _columnSearchCache.get(colUid)?.query || ''
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const searchInputRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Auto-trigger rename on mount when column was just created
  useEffect(() => {
    if (autoRename) {
      setTempTitle(column.title);
      setIsRenaming(true);
      onAutoRenameDone?.();
    }
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen) {
      const t = setTimeout(() => searchInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isSearchOpen]);

  // Persist search state to module-level cache so it survives remount
  useEffect(() => {
    _columnSearchCache.set(colUid, { isOpen: isSearchOpen, query: searchQuery });
  }, [colUid, isSearchOpen, searchQuery]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleConfirmRename = () => {
    if (tempTitle.trim() && tempTitle.trim() !== column.title) {
      onRenameColumn(colUid, tempTitle.trim());
    }
    setIsRenaming(false);
  };

  const handleOpenSearch = () => setIsSearchOpen(true);

  const handleCloseSearch = () => {
    setSearchQuery('');
    setIsSearchOpen(false);
  };

  // Settings icon: visible for admin/owner always; for guests, only when allowColumnEdit !== false
  const showSettings = canManage || allowColumnEdit !== false;

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Filter cards in real-time — no server call
  const filteredCards = searchQuery.trim()
    ? cards.filter(card => matchesSearch(card, searchQuery))
    : cards;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div
        ref={innerRef}
        {...draggableProps}
        style={{
          minWidth: 280,
          maxWidth: 280,
          flexShrink: 0,
          background: colBg,
          borderRadius: 8,
          display: 'flex',
          flexDirection: 'column',
          // Merge DnD transform/transition styles (required for smooth drag animation)
          ...draggableProps?.style,
        }}
      >
        {/* Column header */}
        <div
          {...(columnDraggable && !isRenaming && !isSearchOpen ? dragHandleProps : {})}
          style={{
            padding: '12px 16px',
            borderBottom: `3px solid ${colColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: 63,
            boxSizing: 'border-box',
            cursor: columnDraggable && !isRenaming && !isSearchOpen ? 'grab' : 'default',
            overflow: 'hidden',
          }}
        >
          {isRenaming ? (
            // Rename mode: full-width input
            <Input
              autoFocus
              size="small"
              value={tempTitle}
              onChange={e => setTempTitle(e.target.value)}
              onBlur={handleConfirmRename}
              onKeyDown={e => {
                if (e.key === 'Enter') handleConfirmRename();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
              style={{ width: '100%' }}
            />
          ) : isSearchOpen ? (
            // Search mode: input fills remaining space, icons aligned right
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
              <style>{`@keyframes crm-search-in { from { opacity:0; transform:translateX(10px); } to { opacity:1; transform:translateX(0); } }`}</style>
              <Input
                ref={searchInputRef}
                size="small"
                placeholder={`${translateX('search')}...`}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && handleCloseSearch()}
                style={{ flex: 1, animation: 'crm-search-in 0.2s ease' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                <CloseOutlined
                  style={{ fontSize: 14, cursor: 'pointer' }}
                  onClick={handleCloseSearch}
                />
                {showSettings && (
                  <Tooltip title={translateX('column_properties')}>
                    <SettingOutlined
                      style={{ fontSize: 14, cursor: 'pointer' }}
                      onClick={() => setShowProperties(true)}
                    />
                  </Tooltip>
                )}
              </div>
            </div>
          ) : (
            // Normal mode: title + icons
            <>
              <div className="d-flex align-items-center pl-2">
                <Tooltip title={column.description || undefined}>
                  <span className="font-weight-semibold">{column.title}</span>
                </Tooltip>
                <Badge
                  count={filteredCards.length}
                  showZero
                  style={{ marginLeft: 8, backgroundColor: colColor }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <SearchOutlined
                  style={{ fontSize: 14, cursor: 'pointer' }}
                  onClick={handleOpenSearch}
                />
                {showSettings && (
                  <Tooltip title={translateX('column_properties')}>
                    <SettingOutlined
                      style={{ fontSize: 14, cursor: 'pointer' }}
                      onClick={() => setShowProperties(true)}
                    />
                  </Tooltip>
                )}
              </div>
            </>
          )}
        </div>

        {/* Droppable area */}
        <Droppable droppableId={colUid}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                flex: 1,
                padding: 8,
                minHeight: 60,
                background: snapshot.isDraggingOver ? colBgDragOver : 'transparent',
                transition: 'background-color 0.2s ease',
              }}
            >
              {filteredCards.map((card, index) => (
                <KanbanCard
                  key={card._id}
                  card={card}
                  index={index}
                  boardUid={boardUid}
                  columnUid={colUid}
                  columnSla={column.automation?.sla}
                  onRefresh={onRefresh}
                  isLast={index === filteredCards.length - 1}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>

      {/* Column properties modal */}
      <ColumnPropertiesModal
        show={showProperties}
        setShow={setShowProperties}
        column={column}
        boardUid={boardUid}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default KanbanColumn;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) || 0;
  const g = parseInt(h.substring(2, 4), 16) || 0;
  const b = parseInt(h.substring(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function matchesSearch(card, query) {
  const q = query.toLowerCase().trim();
  if (!q) return true;

  const fields = [
    card.opportunity,
    card.__entity?.name,
    card.__entity?.firstName,
    card.__entity?.fullName,
    card.__entity?.cnpj,
    card.__entity?.cpf,
    card.__entity?.phone?.value,
    card.__entity?.email?.value,
    card.__personContact?.name,
    card.__personContact?.firstName,
    card.__personContact?.fullName,
    card.__personContact?.phone?.value,
    card.__personContact?.email?.value,
    card.__consultant?.name,
    card.__consultant?.fullName,
  ];

  return fields.some(f => f && String(f).toLowerCase().includes(q));
}
